import type { DragEndEvent, DragMoveEvent } from "@dnd-kit/core";
import { Ad } from "@models/Ad.model";
import type { ContentCatalogFolder } from "@models/contentCatalogFolder.model";
import { contentCatalogStore } from "./contentCatalogStore";

export type CatalogSection = "all" | "collection";
export type CatalogView = "tree" | "main";

export type FolderDragItem = {
  kind: "folder";
  section: CatalogSection;
  source: CatalogView;
  folderId: number;
  parentFolderId: number | null;
};

export type AdDragItem = {
  kind: "ad";
  section: CatalogSection;
  source: CatalogView;
  adId: number;
  parentFolderId: number;
};

export type CatalogDragItem = FolderDragItem | AdDragItem;

export type FolderItemDropTarget = {
  kind: "folder-item";
  section: CatalogSection;
  folderId: number;
  parentFolderId: number | null;
};

export type FolderContainerDropTarget = {
  kind: "folder-container";
  section: CatalogSection;
  parentFolderId: number | null;
};

export type AdItemDropTarget = {
  kind: "ad-item";
  section: CatalogSection;
  adId: number;
  parentFolderId: number;
};

export type AdContainerDropTarget = {
  kind: "ad-container";
  section: CatalogSection;
  parentFolderId: number;
};

export type CatalogDropTarget =
  | FolderItemDropTarget
  | FolderContainerDropTarget
  | AdItemDropTarget
  | AdContainerDropTarget;

export type CatalogPersistAction =
  | {
      kind: "folder-move";
      folderId: number;
      newParentFolderId: number | null;
      newIndex: number;
    }
  | {
      kind: "folder-reorder";
      parentFolderId: number;
      orderedIds: number[];
    }
  | {
      kind: "ad-link";
      folderId: number;
      adId: number;
    }
  | {
      kind: "collection-ad-move";
      adId: number;
      sourceFolderId: number;
      targetFolderId: number;
    };

export function createCatalogDragId(item: CatalogDragItem): string {
  if (item.kind === "folder") {
    return [
      "cc",
      "drag",
      item.source,
      "folder",
      item.section,
      item.parentFolderId ?? "root",
      item.folderId,
    ].join(":");
  }

  return [
    "cc",
    "drag",
    item.source,
    "ad",
    item.section,
    item.parentFolderId,
    item.adId,
  ].join(":");
}

export function createCatalogDropId(
  view: CatalogView,
  target: CatalogDropTarget,
  scope = "default",
): string {
  if (target.kind === "folder-item") {
    return [
      "cc",
      "drop",
      view,
      scope,
      "folder-item",
      target.section,
      target.parentFolderId ?? "root",
      target.folderId,
    ].join(":");
  }

  if (target.kind === "folder-container") {
    return [
      "cc",
      "drop",
      view,
      scope,
      "folder-container",
      target.section,
      target.parentFolderId ?? "root",
    ].join(":");
  }

  if (target.kind === "ad-item") {
    return [
      "cc",
      "drop",
      view,
      scope,
      "ad-item",
      target.section,
      target.parentFolderId,
      target.adId,
    ].join(":");
  }

  return [
    "cc",
    "drop",
    view,
    scope,
    "ad-container",
    target.section,
    target.parentFolderId,
  ].join(":");
}

export function isCatalogDragItem(value: unknown): value is CatalogDragItem {
  if (!value || typeof value !== "object") return false;
  const item = value as Partial<CatalogDragItem>;

  if (item.kind === "folder") {
    return (
      (item.section === "all" || item.section === "collection") &&
      (item.source === "tree" || item.source === "main") &&
      typeof item.folderId === "number" &&
      (typeof item.parentFolderId === "number" || item.parentFolderId === null)
    );
  }

  if (item.kind === "ad") {
    return (
      (item.section === "all" || item.section === "collection") &&
      (item.source === "tree" || item.source === "main") &&
      typeof item.adId === "number" &&
      typeof item.parentFolderId === "number"
    );
  }

  return false;
}

export function isCatalogDropTarget(
  value: unknown,
): value is CatalogDropTarget {
  if (!value || typeof value !== "object") return false;
  const target = value as Partial<CatalogDropTarget>;

  if (target.kind === "folder-item") {
    return (
      (target.section === "all" || target.section === "collection") &&
      typeof target.folderId === "number" &&
      (typeof target.parentFolderId === "number" ||
        target.parentFolderId === null)
    );
  }

  if (target.kind === "folder-container") {
    return (
      (target.section === "all" || target.section === "collection") &&
      (typeof target.parentFolderId === "number" ||
        target.parentFolderId === null)
    );
  }

  if (target.kind === "ad-item") {
    return (
      (target.section === "all" || target.section === "collection") &&
      typeof target.adId === "number" &&
      typeof target.parentFolderId === "number"
    );
  }

  if (target.kind === "ad-container") {
    return (
      (target.section === "all" || target.section === "collection") &&
      typeof target.parentFolderId === "number"
    );
  }

  return false;
}

type DropPlacement = "before" | "after";
type FolderDropIntent = "nest" | "reorder";
type CatalogDragEventLike = Pick<DragEndEvent, "active" | "over" | "delta">;
type PointerPosition = { x: number; y: number };

type FolderLookup = {
  folder: ContentCatalogFolder;
  parentFolderId: number | null;
};

type AdDropResolution = {
  section: CatalogSection;
  parentFolderId: number;
  mode: "append" | "relative";
  targetAdId: number | null;
};

type MutationResult = {
  changed: boolean;
  action: CatalogPersistAction | null;
};

let activePointerPosition: PointerPosition | null = null;

export function setCatalogPointerPosition(position: PointerPosition): void {
  activePointerPosition = position;
}

export function clearCatalogPointerPosition(): void {
  activePointerPosition = null;
}

function noMutation(): MutationResult {
  return { changed: false, action: null };
}

function withMutation(action: CatalogPersistAction): MutationResult {
  return { changed: true, action };
}

function getSectionRoots(section: CatalogSection): ContentCatalogFolder[] {
  return (
    section === "all"
      ? contentCatalogStore.folders
      : contentCatalogStore.collections
  ) as ContentCatalogFolder[];
}

function findFolderWithParent(
  nodes: ContentCatalogFolder[],
  folderId: number,
  parentFolderId: number | null = null,
): FolderLookup | null {
  for (const node of nodes) {
    if (node.id === folderId) {
      return { folder: node, parentFolderId };
    }

    const found = findFolderWithParent(node.children || [], folderId, node.id);
    if (found) return found;
  }

  return null;
}

function findFolderById(
  nodes: ContentCatalogFolder[],
  folderId: number,
): ContentCatalogFolder | null {
  for (const node of nodes) {
    if (node.id === folderId) return node;
    const found = findFolderById(node.children || [], folderId);
    if (found) return found;
  }

  return null;
}

function findChildrenListByParentId(
  nodes: ContentCatalogFolder[],
  parentFolderId: number | null,
): ContentCatalogFolder[] | null {
  if (parentFolderId === null) return nodes;
  const parentFolder = findFolderById(nodes, parentFolderId);
  return parentFolder ? parentFolder.children : null;
}

function folderContainsDescendantId(
  folder: ContentCatalogFolder,
  descendantId: number,
): boolean {
  for (const child of folder.children || []) {
    if (child.id === descendantId) return true;
    if (folderContainsDescendantId(child, descendantId)) return true;
  }

  return false;
}

function getDropPlacement(event: CatalogDragEventLike): DropPlacement {
  if (!event.over) return "before";

  if (activePointerPosition) {
    const overTop = event.over.rect.top;
    const overHeight = Math.max(event.over.rect.height, 1);
    const normalizedY = (activePointerPosition.y - overTop) / overHeight;

    if (normalizedY <= 0.45) return "before";
    if (normalizedY >= 0.55) return "after";
  }

  const initialRect = event.active.rect.current.initial;
  const translatedRect = event.active.rect.current.translated;

  if (!initialRect && !translatedRect) return "before";

  const activeTop = translatedRect?.top ?? initialRect?.top ?? 0;
  const activeHeight = translatedRect?.height ?? initialRect?.height ?? 0;
  const activeLeft = translatedRect?.left ?? initialRect?.left ?? 0;
  const activeWidth = translatedRect?.width ?? initialRect?.width ?? 0;

  const activeMid = activeTop + activeHeight / 2;
  const activeMidX = activeLeft + activeWidth / 2;
  const overMid = event.over.rect.top + event.over.rect.height / 2;
  const overMidX = event.over.rect.left + event.over.rect.width / 2;

  const verticalDelta = Math.abs(activeMid - overMid);
  if (verticalDelta <= event.over.rect.height / 3) {
    return activeMidX > overMidX ? "after" : "before";
  }

  return activeMid > overMid ? "after" : "before";
}

function getFolderDropIntent(
  event: CatalogDragEventLike,
  dragItem: FolderDragItem,
  target: FolderItemDropTarget,
): FolderDropIntent {
  if (!event.over) return "reorder";
  if (dragItem.folderId === target.folderId) return "reorder";

  const initialRect = event.active.rect.current.initial;
  const translatedRect = event.active.rect.current.translated;
  const overRect = event.over.rect;

  if (!initialRect && !translatedRect) return "reorder";

  const activeTop = translatedRect?.top ?? initialRect?.top ?? 0;
  const activeHeight = translatedRect?.height ?? initialRect?.height ?? 0;
  const activeLeft = translatedRect?.left ?? initialRect?.left ?? 0;
  const activeWidth = translatedRect?.width ?? initialRect?.width ?? 0;

  const activeMidX = activeLeft + activeWidth / 2;
  const activeMidY = activeTop + activeHeight / 2;
  const pointerX = activePointerPosition?.x ?? activeMidX;
  const pointerY = activePointerPosition?.y ?? activeMidY;

  const relX = (pointerX - overRect.left) / Math.max(overRect.width, 1);
  const relY = (pointerY - overRect.top) / Math.max(overRect.height, 1);

  const inCenterBand =
    relX >= 0.3 && relX <= 0.7 && relY >= 0.3 && relY <= 0.7;

  const pushedRight = event.delta.x > 18;

  return inCenterBand || pushedRight ? "nest" : "reorder";
}

export function getFolderItemDropFeedback(
  event: CatalogDragEventLike,
  dragItem: CatalogDragItem,
  target: FolderItemDropTarget,
): "onto" | "before" | "after" | "" {
  if (dragItem.kind === "folder") {
    if (dragItem.section !== "all" || target.section !== "all") return "";
    if (dragItem.folderId === target.folderId) return "";

    const intent = getFolderDropIntent(event, dragItem, target);
    if (intent === "nest") return "onto";

    const placement = getDropPlacement(event);
    return placement === "before" ? "before" : "after";
  }

  if (dragItem.kind === "ad") {
    const sameSection = dragItem.section === target.section;
    const copyAcross =
      dragItem.section === "all" && target.section === "collection";
    return sameSection || copyAcross ? "onto" : "";
  }

  return "";
}

function moveFolderIntoContainer(
  dragItem: FolderDragItem,
  target: FolderContainerDropTarget,
): MutationResult {
  if (dragItem.section !== "all" || target.section !== "all") return noMutation();

  const roots = getSectionRoots("all");
  const sourceLookup = findFolderWithParent(roots, dragItem.folderId);
  if (!sourceLookup) return noMutation();

  if (target.parentFolderId === dragItem.folderId) return noMutation();
  if (
    target.parentFolderId !== null &&
    folderContainsDescendantId(sourceLookup.folder, target.parentFolderId)
  ) {
    return noMutation();
  }

  const sourceList = findChildrenListByParentId(
    roots,
    sourceLookup.parentFolderId,
  );
  if (!sourceList) return noMutation();

  const sourceIndex = sourceList.findIndex(
    (folder) => folder.id === dragItem.folderId,
  );
  if (sourceIndex < 0) return noMutation();

  if (sourceLookup.parentFolderId === target.parentFolderId) {
    if (sourceIndex === sourceList.length - 1) return noMutation();

    const [movedFolder] = sourceList.splice(sourceIndex, 1);
    sourceList.push(movedFolder);
    const newIndex = sourceList.length - 1;

    if (target.parentFolderId === null) {
      return withMutation({
        kind: "folder-move",
        folderId: dragItem.folderId,
        newParentFolderId: null,
        newIndex,
      });
    }

    return withMutation({
      kind: "folder-reorder",
      parentFolderId: target.parentFolderId,
      orderedIds: sourceList.map((folder) => folder.id),
    });
  }

  const [movedFolder] = sourceList.splice(sourceIndex, 1);
  const targetList = findChildrenListByParentId(roots, target.parentFolderId);

  if (!targetList) {
    sourceList.splice(sourceIndex, 0, movedFolder);
    return noMutation();
  }

  targetList.push(movedFolder);

  return withMutation({
    kind: "folder-move",
    folderId: dragItem.folderId,
    newParentFolderId: target.parentFolderId,
    newIndex: targetList.length - 1,
  });
}

function moveFolderOverFolder(
  dragItem: FolderDragItem,
  target: FolderItemDropTarget,
  placement: DropPlacement,
  intent: FolderDropIntent,
): MutationResult {
  if (dragItem.section !== "all" || target.section !== "all") return noMutation();
  if (dragItem.folderId === target.folderId) return noMutation();

  const roots = getSectionRoots("all");
  const sourceLookup = findFolderWithParent(roots, dragItem.folderId);
  if (!sourceLookup) return noMutation();

  if (folderContainsDescendantId(sourceLookup.folder, target.folderId)) {
    return noMutation();
  }

  if (intent === "nest") {
    const sourceList = findChildrenListByParentId(
      roots,
      sourceLookup.parentFolderId,
    );
    if (!sourceList) return noMutation();

    const sourceIndex = sourceList.findIndex(
      (folder) => folder.id === dragItem.folderId,
    );
    if (sourceIndex < 0) return noMutation();

    const targetFolder = findFolderById(roots, target.folderId);
    if (!targetFolder) return noMutation();

    const [movedFolder] = sourceList.splice(sourceIndex, 1);
    targetFolder.children.push(movedFolder);

    return withMutation({
      kind: "folder-move",
      folderId: dragItem.folderId,
      newParentFolderId: target.folderId,
      newIndex: targetFolder.children.length - 1,
    });
  }

  const sourceList = findChildrenListByParentId(
    roots,
    sourceLookup.parentFolderId,
  );
  const targetList = findChildrenListByParentId(roots, target.parentFolderId);

  if (!sourceList || !targetList) return noMutation();

  const sourceIndex = sourceList.findIndex(
    (folder) => folder.id === dragItem.folderId,
  );
  if (sourceIndex < 0) return noMutation();

  if (sourceLookup.parentFolderId === target.parentFolderId) {
    const siblingList = findChildrenListByParentId(roots, target.parentFolderId);
    if (!siblingList) return noMutation();

    const localSourceIndex = siblingList.findIndex(
      (folder) => folder.id === dragItem.folderId,
    );
    const targetIndex = siblingList.findIndex(
      (folder) => folder.id === target.folderId,
    );
    if (localSourceIndex < 0 || targetIndex < 0) return noMutation();
    if (localSourceIndex === targetIndex) return noMutation();

    const [movedFolder] = siblingList.splice(localSourceIndex, 1);
    let insertIndex = targetIndex;

    if (localSourceIndex < targetIndex) insertIndex -= 1;
    if (placement === "after") insertIndex += 1;

    const boundedIndex = Math.max(0, Math.min(insertIndex, siblingList.length));
    siblingList.splice(boundedIndex, 0, movedFolder);

    if (target.parentFolderId === null) {
      return withMutation({
        kind: "folder-move",
        folderId: dragItem.folderId,
        newParentFolderId: null,
        newIndex: boundedIndex,
      });
    }

    return withMutation({
      kind: "folder-reorder",
      parentFolderId: target.parentFolderId,
      orderedIds: siblingList.map((folder) => folder.id),
    });
  }

  const [movedFolder] = sourceList.splice(sourceIndex, 1);
  const targetIndex = targetList.findIndex((folder) => folder.id === target.folderId);

  if (targetIndex < 0) {
    sourceList.splice(sourceIndex, 0, movedFolder);
    return noMutation();
  }

  const insertIndex = placement === "before" ? targetIndex : targetIndex + 1;
  const boundedIndex = Math.max(0, Math.min(insertIndex, targetList.length));
  targetList.splice(boundedIndex, 0, movedFolder);

  return withMutation({
    kind: "folder-move",
    folderId: dragItem.folderId,
    newParentFolderId: target.parentFolderId,
    newIndex: boundedIndex,
  });
}

function resolveAdDropTarget(target: CatalogDropTarget): AdDropResolution | null {
  if (target.kind === "folder-item") {
    return {
      section: target.section,
      parentFolderId: target.folderId,
      mode: "append",
      targetAdId: null,
    };
  }

  if (target.kind === "ad-container") {
    return {
      section: target.section,
      parentFolderId: target.parentFolderId,
      mode: "append",
      targetAdId: null,
    };
  }

  if (target.kind === "ad-item") {
    return {
      section: target.section,
      parentFolderId: target.parentFolderId,
      mode: "relative",
      targetAdId: target.adId,
    };
  }

  if (target.kind === "folder-container" && target.parentFolderId !== null) {
    return {
      section: target.section,
      parentFolderId: target.parentFolderId,
      mode: "append",
      targetAdId: null,
    };
  }

  return null;
}

function cloneAd(ad: Ad): Ad {
  return new Ad({
    ...ad,
    children: [...(ad.children || [])],
  });
}

function moveAdAcrossFolders(
  sourceFolder: ContentCatalogFolder,
  targetFolder: ContentCatalogFolder,
  adId: number,
  targetAdId: number | null,
  mode: "append" | "relative",
  placement: DropPlacement,
): boolean {
  const sourceIndex = sourceFolder.ads.findIndex((ad) => ad.id === adId);
  if (sourceIndex < 0) return false;

  const [movedAd] = sourceFolder.ads.splice(sourceIndex, 1);

  if (mode === "append") {
    targetFolder.ads.push(movedAd);
    return true;
  }

  if (targetAdId === null) {
    sourceFolder.ads.splice(sourceIndex, 0, movedAd);
    return false;
  }

  const targetIndex = targetFolder.ads.findIndex((ad) => ad.id === targetAdId);
  if (targetIndex < 0) {
    sourceFolder.ads.splice(sourceIndex, 0, movedAd);
    return false;
  }

  const insertIndex = placement === "before" ? targetIndex : targetIndex + 1;
  const boundedIndex = Math.max(
    0,
    Math.min(insertIndex, targetFolder.ads.length),
  );
  targetFolder.ads.splice(boundedIndex, 0, movedAd);
  return true;
}

function moveOrCopyAd(
  dragItem: AdDragItem,
  target: CatalogDropTarget,
  placement: DropPlacement,
): MutationResult {
  const resolvedTarget = resolveAdDropTarget(target);
  if (!resolvedTarget) return noMutation();

  const isCrossSection = dragItem.section !== resolvedTarget.section;
  const isAllowedCrossDirection =
    dragItem.section === "all" && resolvedTarget.section === "collection";

  if (isCrossSection && !isAllowedCrossDirection) return noMutation();

  const sourceRoots = getSectionRoots(dragItem.section);
  const sourceFolder = findFolderById(sourceRoots, dragItem.parentFolderId);
  if (!sourceFolder) return noMutation();

  const sourceAd = sourceFolder.ads.find((ad) => ad.id === dragItem.adId);
  if (!sourceAd) return noMutation();

  const targetRoots = getSectionRoots(resolvedTarget.section);
  const targetFolder = findFolderById(
    targetRoots,
    resolvedTarget.parentFolderId,
  );
  if (!targetFolder) return noMutation();

  if (!isCrossSection) {
    // Same-folder ad reorder is intentionally disabled.
    if (sourceFolder.id === targetFolder.id) return noMutation();

    const moved = moveAdAcrossFolders(
      sourceFolder,
      targetFolder,
      dragItem.adId,
      resolvedTarget.targetAdId,
      resolvedTarget.mode,
      placement,
    );

    if (!moved) return noMutation();

    return withMutation({
      kind: "collection-ad-move",
      adId: dragItem.adId,
      sourceFolderId: sourceFolder.id,
      targetFolderId: targetFolder.id,
    });
  }

  if (targetFolder.ads.some((ad) => ad.id === dragItem.adId)) return noMutation();

  const copiedAd = cloneAd(sourceAd);
  let insertIndex = targetFolder.ads.length;

  if (resolvedTarget.mode === "relative" && resolvedTarget.targetAdId !== null) {
    const targetIndex = targetFolder.ads.findIndex(
      (ad) => ad.id === resolvedTarget.targetAdId,
    );
    if (targetIndex < 0) return noMutation();
    insertIndex = placement === "before" ? targetIndex : targetIndex + 1;
  }

  const boundedIndex = Math.max(
    0,
    Math.min(insertIndex, targetFolder.ads.length),
  );
  targetFolder.ads.splice(boundedIndex, 0, copiedAd);

  return withMutation({
    kind: "ad-link",
    folderId: targetFolder.id,
    adId: dragItem.adId,
  });
}

export function handleCatalogDragEnd(
  event: DragEndEvent,
): CatalogPersistAction | null {
  if (!event.over) return null;

  const dragData = event.active.data.current;
  const dropData = event.over.data.current;

  if (!isCatalogDragItem(dragData) || !isCatalogDropTarget(dropData)) return null;

  const placement = getDropPlacement(event);

  if (dragData.kind === "folder") {
    if (dropData.kind === "folder-item") {
      const intent = getFolderDropIntent(event, dragData, dropData);
      const result = moveFolderOverFolder(dragData, dropData, placement, intent);
      return result.changed ? result.action : null;
    }

    if (dropData.kind === "folder-container") {
      const result = moveFolderIntoContainer(dragData, dropData);
      return result.changed ? result.action : null;
    }

    return null;
  }

  const result = moveOrCopyAd(dragData, dropData, placement);
  return result.changed ? result.action : null;
}

export function getCatalogDragHint(event: DragMoveEvent): string {
  if (!event.over) return "";

  const dragData = event.active.data.current;
  const dropData = event.over.data.current;
  if (!isCatalogDragItem(dragData) || !isCatalogDropTarget(dropData)) return "";

  if (dragData.kind === "folder") {
    if (dropData.kind === "folder-item") {
      const intent = getFolderDropIntent(event, dragData, dropData);
      return intent === "nest"
        ? "Drop to nest folder"
        : "Drop to reorder folder";
    }

    if (dropData.kind === "folder-container") {
      return "Drop to move folder to this level";
    }

    return "";
  }

  const resolvedTarget = resolveAdDropTarget(dropData);
  if (!resolvedTarget) return "";

  if (
    dragData.section === resolvedTarget.section &&
    dragData.parentFolderId === resolvedTarget.parentFolderId
  ) {
    return "Same-folder ad reorder is disabled";
  }

  if (dragData.section === "all" && resolvedTarget.section === "collection") {
    return "Drop to copy ad into collection";
  }

  return "Drop to move ad";
}
